/**
 * variant-media-fallback-lunkrbytes.js
 *
 * When the selected option combination does NOT resolve to a variant (the size
 * grid is sparse: "Hooded Long Sleeve – Black" has no S), Refresh/Dawn's
 * product-info.js gives up before touching the gallery:
 *
 *   handleUpdateProductInfo: if (!variant) { this.setUnavailable(); return; }
 *
 * That return sits BEFORE updateMedia() and before variantChange is published,
 * so clicking a Shirt Style while the checked Size doesn't exist for it never
 * moves the gallery image. This script fills exactly that gap: it finds the
 * first existing variant sharing the longest leading run of the selected
 * option values (Shirt Style at minimum; Shirt Style + Print Color preferred
 * on the 3-option product) and shows THAT variant's featured image.
 *
 * Data source: snippets/variant-media-map-lunkrbytes.liquid embeds a JSON map
 * (only variants that HAVE a featured image) inside <product-info>. If the map
 * is absent or empty the script does nothing, and every path is guarded so it
 * degrades to a no-op rather than throwing inside the picker's event chain.
 *
 * The Dawn traps this must survive (long version in assets/garment-spec.js):
 *
 * 1. PUB_SUB_EVENTS.variantChange never fires in the state we care about (no
 *    variant resolved), so we subscribe to optionValueSelectionChange, which
 *    VariantSelects publishes on every option click regardless.
 * 2. The selection is read from the CHECKED radios / SELECTED <select>s, never
 *    from [data-selected-variant] (null in this state). Dawn builds the radio
 *    name with {%- capture -%} and the capture keeps a TRAILING NEWLINE
 *    ("Shirt Style-1\n"), so names are always .trim()ed — and always compared
 *    via the `name` PROPERTY, because the option labels contain an en dash and
 *    a CSS attribute selector would need escaping.
 * 3. <variant-selects> is wholesale-replaced on every change
 *    (HTMLUpdateUtility), so nothing is bound to it: the pub-sub subscription
 *    is global and the picker is re-queried from <product-info> at event time.
 *
 * Why the gallery move sometimes needs a fetch: the product templates ship
 * hide_variants: true, so other styles' images are NOT in the gallery DOM
 * (product-media-gallery.liquid skips them server-side). Stock Dawn handles
 * that by fetching the section render and letting product-info's updateMedia()
 * diff the fetched <media-gallery ul> into the live one — so we do exactly the
 * same with ?variant=<fallback id>&section_id=<id>, reusing updateMedia()
 * itself. When the image IS already in the DOM (hide_variants off, or shared
 * media) we skip the round-trip and call media-gallery's setActiveMedia()
 * directly with the `${sectionId}-${mediaId}` key product-info.js uses.
 */
(() => {
  if (window.variantMediaFallbackLunkrbytesLoaded) return;
  window.variantMediaFallbackLunkrbytesLoaded = true;

  if (
    typeof subscribe !== 'function' ||
    typeof PUB_SUB_EVENTS === 'undefined' ||
    !PUB_SUB_EVENTS.optionValueSelectionChange
  ) {
    return;
  }

  /** One in-flight fallback fetch per <product-info>; a newer click aborts it. */
  const abortControllers = new WeakMap();

  const readMap = (productInfo) => {
    const node = productInfo.querySelector('script[data-variant-media-map]');
    if (!node) return null;
    try {
      const map = JSON.parse(node.textContent);
      if (!map || !Array.isArray(map.options) || map.options.length === 0) return null;
      if (!Array.isArray(map.variants) || map.variants.length === 0) return null;
      return map;
    } catch (error) {
      return null;
    }
  };

  /**
   * The currently selected option values, in option order, straight from the
   * picker inputs (trap 2: trim the name, compare the property).
   */
  const readSelectedValues = (productInfo, optionNames) => {
    const picker = productInfo.querySelector('variant-selects');
    if (!picker) return null;

    const selects = Array.from(picker.querySelectorAll('select'));
    const checkedRadios = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));

    const values = [];
    for (let index = 0; index < optionNames.length; index++) {
      const optionName = optionNames[index];
      const radioName = `${optionName}-${index + 1}`; // Dawn: "{{ option.name }}-{{ option.position }}", 1-based
      const selectName = `options[${optionName}]`;

      const select = selects.find((el) => (el.name || '').trim() === selectName);
      if (select) {
        values.push(select.value);
        continue;
      }

      const radio = checkedRadios.find((el) => (el.name || '').trim() === radioName);
      if (radio) {
        values.push(radio.value);
        continue;
      }

      return null; // can't read this option — bail out rather than guess
    }
    return values;
  };

  const leadingMatchLength = (variantOptions, selectedValues) => {
    if (!Array.isArray(variantOptions)) return 0;
    let length = 0;
    while (length < selectedValues.length && variantOptions[length] === selectedValues[length]) length++;
    return length;
  };

  /**
   * Direct path: the fallback media is already in the gallery DOM.
   * hide_variants keeps other variants' media OUT of the DOM, and
   * setActiveMedia falls back to the first slide (and dereferences a missing
   * thumbnail) when asked for an id that isn't there — so this path is only
   * taken when both the slide and, if thumbnails render, its thumbnail exist.
   */
  const activateInPlace = (productInfo, mediaKey) => {
    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return false;

    if (!gallery.querySelector(`[data-media-id="${mediaKey}"]`)) return false;
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbnails && !thumbnails.querySelector(`[data-target="${mediaKey}"]`)) return false;

    gallery.setActiveMedia(mediaKey, true); // `true` = prepend, exactly as product-info.js passes it
    return true;
  };

  /**
   * Fetch path: the fallback media is not in the DOM (hide_variants). Fetch
   * the section rendered for the fallback variant and reuse product-info's
   * own updateMedia() to diff it in and activate it — the same code path a
   * resolving variant change takes.
   */
  const fetchAndSync = (productInfo, entry) => {
    if (typeof productInfo.updateMedia !== 'function' || !productInfo.dataset.url) return;

    const controller = new AbortController();
    abortControllers.set(productInfo, controller);

    const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
    const requestUrl = `${productInfo.dataset.url}?variant=${entry.id}&section_id=${sectionId}`;

    fetch(requestUrl, { signal: controller.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((responseText) => {
        if (!productInfo.isConnected) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        productInfo.updateMedia(html, entry.media);
      })
      .catch(() => {
        // AbortError from a faster next click, or a network hiccup: the
        // gallery simply stays where it was.
      });
  };

  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
    try {
      const eventTarget = data?.event?.target || data?.target;
      if (!eventTarget || typeof eventTarget.closest !== 'function') return;

      const productInfo = eventTarget.closest('product-info');
      if (!productInfo || !productInfo.dataset.section) return;

      // Whatever this click leads to supersedes any fallback fetch in flight.
      abortControllers.get(productInfo)?.abort();

      // Combined-listing clicks swap the whole <product-info>; stay out.
      const targetProductUrl = data?.target?.dataset?.productUrl;
      if (targetProductUrl && targetProductUrl !== productInfo.dataset.url) return;

      const map = readMap(productInfo);
      if (!map) return;

      const selectedValues = readSelectedValues(productInfo, map.options);
      if (!selectedValues) return;

      let best = null;
      let bestLength = 0;
      for (const entry of map.variants) {
        if (!entry || entry.media == null || entry.id == null) continue;
        const length = leadingMatchLength(entry.options, selectedValues);
        // Exact match: the combo resolves to a variant WITH an image — stock
        // product-info.js will move the gallery itself. Do nothing.
        if (length === selectedValues.length) return;
        if (length > bestLength) {
          best = entry;
          bestLength = length;
        }
      }
      // Nothing shares even option1 with the selection: leave the gallery alone.
      if (!best || bestLength < 1) return;

      const mediaKey = `${productInfo.dataset.section}-${best.media}`;
      if (activateInPlace(productInfo, mediaKey)) return;
      fetchAndSync(productInfo, best);
    } catch (error) {
      // Never let a gallery nicety break the variant picker.
    }
  });
})();
